/**
 * InstallGuideScreen — guida visuale all'installazione di Nullum come PWA.
 *
 * Rilevamento piattaforma:
 *   - iOS Safari: passi specifici (Condividi → Aggiungi alla schermata Home)
 *     con dettagli per i tester non-tecnici.
 *   - Android (Chrome/Edge): "Aggiungi a Home" dal menu ⋮.
 *   - Desktop Chrome/Edge: icona "Installa" nella barra indirizzi.
 *
 * Mostra anche un avviso se l'utente NON e' su Safari (su iPhone serve Safari,
 * Chrome/Firefox/etc su iOS NON possono installare PWA).
 */

import { useNavigate } from 'react-router-dom';
import { Frame, Header, Footer, Btn, Box } from '../components/Frame';
import { installContext } from '../lib/platform';
import { SafariCoachmark, InAppNotice } from '../components/IosInstallSheet';

function Step({ n, children }) {
  return (
    <div className="flex gap-3 items-start">
      <div className="shrink-0 w-7 h-7 border border-ink text-ink text-glow flex items-center justify-center text-[14px] font-mono">
        {n}
      </div>
      <div className="flex-1 text-[15px] leading-relaxed">{children}</div>
    </div>
  );
}

export default function InstallGuideScreen() {
  const navigate = useNavigate();
  const ctx = installContext();
  const { ios: isIPhone, android: isAndroid, desktop: isDesktop } = ctx;
  const already = ctx.standalone;

  return (
    <Frame statusLeft="INSTALLA" statusRight="5G">
      <Header title="// Installa Nullum" crumb="◀ indietro" onCrumbClick={() => navigate(-1)} />

      {already && (
        <Box variant="success" className="text-[15px] mb-3">
          ✓ Nullum è già installata. Aprila dall'icona sulla home.
        </Box>
      )}

      <p className="text-[14px] text-ink-dim mb-4">
        Installala e Nullum si apre a schermo intero, con le notifiche (se le
        attivi) e senza digitare l'indirizzo ogni volta. Niente App Store o
        Play Store: parte dal browser.
      </p>

      {/* ============================================================ */}
      {isIPhone && (
        <Box className="mb-3 space-y-3">
          <p className="text-ink text-glow text-[16px]">📱 iPhone / iPad</p>

          {ctx.iosNeedsSafari
            ? <InAppNotice appName={ctx.inApp || ctx.otherBrowser} />
            : <SafariCoachmark />}

          <p className="text-[13px] text-ink-dim pt-2 border-t border-ink-dim/40">
            <span className="text-amber">⚠</span> &nbsp;
            <b>Le notifiche su iPhone</b> arrivano solo dopo aver installato
            Nullum sulla home. Poi apri l'app e attivale dal menu.
          </p>
        </Box>
      )}

      {/* ============================================================ */}
      {isAndroid && (
        <Box className="mb-3 space-y-3">
          <p className="text-ink text-glow text-[16px]">🤖 Android</p>
          <Step n="1">
            Spesso Chrome mostra già un banner "Installa app". Se l'hai
            chiuso, vai al passo 2.
          </Step>
          <Step n="2">
            Tocca i tre puntini <b>⋮</b> in alto a destra di Chrome.
          </Step>
          <Step n="3">
            Scegli <b>"Installa app"</b> oppure <b>"Aggiungi alla schermata
            Home"</b>.
          </Step>
          <Step n="4">
            Conferma. L'icona compare sulla home come ogni app.
          </Step>
        </Box>
      )}

      {/* ============================================================ */}
      {isDesktop && (
        <Box className="mb-3 space-y-3">
          <p className="text-ink text-glow text-[16px]">💻 Computer</p>
          <Step n="1">
            In Chrome / Edge trovi un'icona "Installa" o "+" nella barra
            dell'indirizzo. Cliccala.
          </Step>
          <Step n="2">
            Se non la vedi: menù del browser → "Installa Nullum". L'app si
            apre in una finestra sua e puoi tenerla sulla taskbar.
          </Step>
        </Box>
      )}

      {/* ============================================================ */}
      <Section title="❓ Cosa NON serve">
        <ul className="list-disc pl-5 space-y-1">
          <li>Non serve App Store né Play Store.</li>
          <li>Non serve un account Apple o Google.</li>
          <li>Non serve dare numero di telefono né email.</li>
          <li>Non scarichi niente: il browser tiene una copia, è tutto qui.</li>
        </ul>
      </Section>

      <Section title="❓ Perché non c'è sull'App Store?">
        <p>
          Nullum è una web app: vive nel browser e si installa da lì. Così:
        </p>
        <ul className="list-disc pl-5 space-y-1 mt-1">
          <li>Aggiornamenti immediati, senza passare dallo store.</li>
          <li>Apple e Google non vedono i tuoi messaggi.</li>
          <li>Funziona uguale su iPhone, Android e computer.</li>
        </ul>
      </Section>

      <Footer>
        <Btn variant="ghost" onClick={() => navigate(-1)}>◀ Indietro</Btn>
      </Footer>
    </Frame>
  );
}

function Section({ title, children }) {
  return (
    <Box className="mb-3 text-[14px] text-ink-dim space-y-2">
      <p className="text-ink text-[15px]">{title}</p>
      {children}
    </Box>
  );
}
